import { Component, inject, OnInit } from '@angular/core';
import { ModalBodyClass } from '../../../../../modals/modal-body';
import {
  GroupGlobalPrivilegesFormData,
  GroupTenantPrivilegesFormData,
  PrivilegeRestriction,
  PrivilegeRole,
  PrivilegeScope,
  PrivilegesFormData,
  PrivilegeType,
} from '../privilege-models';
import { TypeaheadConfig } from '../../../../../forms/fields/typeahead/typeahead.component';
import { Microsoft365UserResource } from '../../../../../helpdesk/shared/resources/microsoft-365-user.resource';
import { delayWhen, map } from 'rxjs/operators';
import {
  ZiroFieldConfig,
  ZiroFieldConfigs,
  ZiroFormConfig,
  ZiroFormsUpdate,
  ZiroFormsValidationState,
} from '../../../../../forms/ziro-forms-models';
import { DetailedModalComponent } from '../../../../../modals/detailed-modal/detailed-modal.component';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../../../forms/fields/select/ziro-select.component';
import { MicrosoftSecurityGroupsTenantSearchResource } from '../../../../resources/microsoft-security-groups-tenant-search.resource';
import { ZiroRadioButton, ZiroRadioConfig } from '../../../../../forms/fields/radio/ziro-radio.component';
import { Observable, of } from 'rxjs';
import { HtmlCheckboxType, ZiroCheckboxConfig } from '../../../../../forms/fields/checkbox/ziro-checkbox.component';
import { MicrosoftDialPlanFieldConfig, PrivilegedTenant } from '../../../../../shared/models/generated/ziroModels';
import { UserGroupPrivilegesService } from '../user-group-privileges.service';
import { ViewMembersState } from '../../../../../shared/view-members/view-members.component';
import { HtmlInputType, ZiroTextConfig } from '../../../../../forms/fields/text/ziro-text.component';

export interface UserGroupPrivilegesModalProperties {
  existingEntities: string[];
  entity: PrivilegesFormData;
  isExisting: boolean;
  afterSubmitFn: () => Observable<void>;
  tenantId?: string;
  allTenants?: PrivilegedTenant[];
  dialPlanGroups?: MicrosoftDialPlanFieldConfig[];
}

@Component({
  selector: 'ziro-user-group-privileges-modal',
  standalone: false,
  templateUrl: 'user-group-privileges-modal.component.html',
  styleUrls: ['user-group-privileges-modal.component.scss'],
})
export class UserGroupPrivilegesModalComponent extends ModalBodyClass<PrivilegesFormData> implements OnInit {
  private _modalComponent = inject<DetailedModalComponent<UserGroupPrivilegesModalComponent>>(DetailedModalComponent);
  private _microsoft365UserResource = inject(Microsoft365UserResource);
  private _microsoftSecurityGroupsTenantSearchResource = inject(MicrosoftSecurityGroupsTenantSearchResource);
  private _userGroupPrivilegesService = inject(UserGroupPrivilegesService);

  formConfig: ZiroFormConfig;
  PrivilegeType = PrivilegeType;
  PrivilegeRole = PrivilegeRole;
  PrivilegeScope = PrivilegeScope;

  type: PrivilegeType;
  scope: PrivilegeScope;
  viewMembersState = ViewMembersState.IDLE;
  usersInGroup: string[] = [];
  isPrivilegesDirty = false;

  private _tenantId: string;
  private _allTenants: PrivilegedTenant[];
  private _existingEntities: string[];
  private _dialPlanGroupOptions: ZiroSelectOption[];
  private _afterSubmitFn: () => Observable<void>;

  ngOnInit(): void {
    const modalProperties: UserGroupPrivilegesModalProperties = this._modalComponent.modalViewProperties;
    this._tenantId = modalProperties.tenantId;
    this._allTenants = modalProperties.allTenants;
    this._existingEntities = modalProperties.existingEntities;
    this.type = modalProperties.entity.type;
    this.scope = modalProperties.entity.scope;
    this.isExisting = modalProperties.isExisting;
    if (this.scope === PrivilegeScope.TENANT) {
      this._dialPlanGroupOptions = this._getDialPlanGroupOptions(modalProperties.dialPlanGroups);
    }
    this._afterSubmitFn = modalProperties.afterSubmitFn;

    this._initFormConfig(modalProperties.entity);
    this.entitySource.next(modalProperties.entity);

    if (this.type === PrivilegeType.GROUP) {
      this.ziroFormsUpdate$.subscribe((update) => {
        const groupUpdate = update as ZiroFormsUpdate<GroupTenantPrivilegesFormData | GroupGlobalPrivilegesFormData>;
        if (groupUpdate.new) {
          if (
            groupUpdate.new.scope === PrivilegeScope.GLOBAL &&
            groupUpdate.new.tenantGuid &&
            groupUpdate.new.tenantGuid !== groupUpdate.old?.tenantGuid
          ) {
            const selectedTenant = this._allTenants.find((tenant) => tenant.tenantGuid === groupUpdate.new.tenantGuid);
            this._tenantId = String(selectedTenant.tenantId);
            if (!this.isExisting) {
              this.entitySource.next({ ...groupUpdate.new, displayName: '' });
            }
          }
          if (groupUpdate.new.displayName !== groupUpdate.old?.displayName) {
            this._fetchGroupMembers(this._tenantId, groupUpdate.new.displayName);
          }
        }
      });
    }

    this.ziroFormsUpdate$.subscribe((update) => {
      if (update.new && Object.values(update.new).some((val) => val === true)) {
        setTimeout(() => {
          // timeout to avoid weird flicker
          this.isPrivilegesDirty = true;
        }, 50);
      }
    });

    this.ziroFormsUpdate$.subscribe((update) => {
      if (update.new && update.new.automation && !update.new.teamsUserManagement) {
        this.entitySource.next({ ...update.new, automation: false });
      }
    });
  }

  submit(): Observable<void> {
    this._validateAndSubmitSource.next(false);

    if (this.entity.type === PrivilegeType.USER) {
      this.entity.upn = this.entity.upn?.trim();
    }
    return this._userGroupPrivilegesService
      .savePrivileges(this.entity, this._tenantId, this.isExisting)
      .pipe(delayWhen(() => this._afterSubmitFn()));
  }

  onInvalidSubmit(): Observable<void> {
    this.isPrivilegesDirty = true;
    return of(null);
  }

  private _initFormConfig(entity: PrivilegesFormData): void {
    this.formConfig = {
      fields: {
        role: {
          dataAutomation: 'user-group-privileges-role-radio',
          required: true,
          componentConfig: new ZiroRadioConfig({
            isDisplayVertical: true,
            alignLeftNoLabel: true,
            hideLabel: true,
            buttons: this._getRoleRadioOptions(),
          }),
          validation: [
            {
              validator: (val: PrivilegeRole, ...injected: boolean[]) => {
                if (val === PrivilegeRole.CUSTOM) {
                  const hasConfiguredPrivilege = injected.some((bool) => bool === true);
                  return hasConfiguredPrivilege ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
                }
                return ZiroFormsValidationState.VALID;
              },
              message: 'tkey;admin.user_group_privileges_modal.roles.custom.validation.unconfigured',
              injectValuesFromFields: Object.entries(entity)
                .filter(([, value]) => typeof value === 'boolean')
                .map(([key]) => key),
            },
          ],
        },
        teamsUserManagement: {
          dataAutomation: 'user-group-privileges-teams-user-management-checkbox',
          label: 'tkey;admin.user_group_privileges_modal.privileges.teams_user_management',
          helpText: 'tkey;admin.user_group_privileges_modal.privileges.teams_user_management.helptext',
          componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.LEFT_ALIGNED_CHECKBOX }),
          hidden: () => this.formData.role !== PrivilegeRole.CUSTOM,
        },
        resourceAccountManagement: {
          dataAutomation: 'user-group-privileges-resource-account-management-checkbox',
          label: 'tkey;admin.user_group_privileges_modal.privileges.resource_account_management',
          helpText: 'tkey;admin.user_group_privileges_modal.privileges.resource_account_management.helptext',
          componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.LEFT_ALIGNED_CHECKBOX }),
          hidden: () => this.formData.role !== PrivilegeRole.CUSTOM,
        },
        autoAttendantManagement: {
          dataAutomation: 'user-group-privileges-auto-attendant-management-checkbox',
          label: 'tkey;admin.user_group_privileges_modal.privileges.auto_attendant_management',
          helpText: 'tkey;admin.user_group_privileges_modal.privileges.auto_attendant_management.helptext',
          componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.LEFT_ALIGNED_CHECKBOX }),
          hidden: () => this.formData.role !== PrivilegeRole.CUSTOM,
          disabled: () => true,
          disabledTooltip: 'tkey;admin.user_group_privileges_modal.privileges.admin_disabled',
        },
        callQueueManagement: {
          dataAutomation: 'user-group-privileges-call-queue-management-checkbox',
          label: 'tkey;admin.user_group_privileges_modal.privileges.call_queue_management',
          helpText: 'tkey;admin.user_group_privileges_modal.privileges.call_queue_management.helptext',
          componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.LEFT_ALIGNED_CHECKBOX }),
          hidden: () => this.formData.role !== PrivilegeRole.CUSTOM,
          disabled: () => true,
          disabledTooltip: 'tkey;admin.user_group_privileges_modal.privileges.admin_disabled',
        },
        emergencyLocationManagement: {
          dataAutomation: 'user-group-privileges-emergency-location-management-checkbox',
          label: 'tkey;admin.user_group_privileges_modal.privileges.emergency_location_management',
          helpText: 'tkey;admin.user_group_privileges_modal.privileges.emergency_location_management.helptext',
          componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.LEFT_ALIGNED_CHECKBOX }),
          hidden: () => this.formData.role !== PrivilegeRole.CUSTOM,
        },
        automation: {
          dataAutomation: 'user-group-privileges-automation-checkbox',
          label: 'tkey;admin.user_group_privileges_modal.privileges.automation',
          helpText: 'tkey;admin.user_group_privileges_modal.privileges.automation.helptext',
          componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.LEFT_ALIGNED_CHECKBOX }),
          hidden: () => this.formData.role !== PrivilegeRole.CUSTOM,
          disabled: () => !this.formData.teamsUserManagement,
          disabledTooltip:
            'tkey;admin.user_group_privileges_modal.privileges.automation.validation.teams_user_management',
        },
        configurationReports: {
          dataAutomation: 'user-group-privileges-configuration-reports-checkbox',
          label: 'tkey;admin.user_group_privileges_modal.privileges.configuration_reports',
          helpText: 'tkey;admin.user_group_privileges_modal.privileges.configuration_reports.helptext',
          componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.LEFT_ALIGNED_CHECKBOX }),
          hidden: () => this.formData.role !== PrivilegeRole.CUSTOM,
          disabled: () => true,
          disabledTooltip: 'tkey;admin.user_group_privileges_modal.privileges.admin_disabled',
        },
        auditReports: {
          dataAutomation: 'user-group-privileges-audit-reports-checkbox',
          label: 'tkey;admin.user_group_privileges_modal.privileges.audit_reports',
          helpText: 'tkey;admin.user_group_privileges_modal.privileges.audit_reports.helptext',
          componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.LEFT_ALIGNED_CHECKBOX }),
          hidden: () => this.formData.role !== PrivilegeRole.CUSTOM,
        },
        cdrReports: {
          dataAutomation: 'user-group-privileges-cdr-reports-checkbox',
          label: 'tkey;admin.user_group_privileges_modal.privileges.cdr_reports',
          helpText: 'tkey;admin.user_group_privileges_modal.privileges.cdr_reports.helptext',
          componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.LEFT_ALIGNED_CHECKBOX }),
          hidden: () => this.formData.role !== PrivilegeRole.CUSTOM,
        },
        migration: {
          dataAutomation: 'user-group-privileges-migration-checkbox',
          label: 'tkey;admin.user_group_privileges_modal.privileges.migration',
          helpText: 'tkey;admin.user_group_privileges_modal.privileges.migration.helptext',
          componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.LEFT_ALIGNED_CHECKBOX }),
          hidden: () => this.formData.role !== PrivilegeRole.CUSTOM,
        },
      },
    };

    switch (entity.type) {
      case PrivilegeType.USER:
        this.formConfig.fields.upn = this._getUpnField();
        break;
      case PrivilegeType.GROUP:
        this.formConfig.fields.displayName = this._getGroupNameField();
        break;
    }

    if (entity.scope === PrivilegeScope.TENANT) {
      this.formConfig.fields = {
        ...this.formConfig.fields,
        ...this._getRestrictionFields(),
      };
    }
    if (entity.scope === PrivilegeScope.GLOBAL && entity.type === PrivilegeType.GROUP) {
      this.formConfig.fields.tenantGuid = this._getGroupTenantGuidField();
    }
  }

  private _isDuplicate(val: string): boolean {
    return val && this._existingEntities.some((entity) => val.trim() === entity);
  }

  private _getUpnField(): ZiroFieldConfig<TypeaheadConfig | ZiroTextConfig> {
    return {
      label: 'tkey;admin.user_group_privileges_modal.user.upn',
      dataAutomation: 'user-group-privileges-upn-input',
      required: true,
      disabled: () => this.isExisting,
      disabledTooltip: 'tkey;admin.user_group_privileges_modal.user.upn.disabled',
      componentConfig: this._getUpnComponentConfig(),
      validation: this.isExisting
        ? []
        : [
            {
              validator: (val: string) => {
                if (!!val && !/^\s*[^@\s]+@[^@\s]+\.[^@\s]+[^@\s.]+\s*$/.test(val.trim())) {
                  return ZiroFormsValidationState.INVALID;
                }
                return ZiroFormsValidationState.VALID;
              },
              message: 'tkey;validators.global.upn.invalid.error',
            },
            {
              validator: (val: string) => {
                return this._isDuplicate(val) ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
              },
              message: 'tkey;admin.user_group_privileges_modal.user.upn.unique',
            },
          ],
    };
  }

  private _getUpnComponentConfig(): TypeaheadConfig | ZiroTextConfig {
    switch (this.scope) {
      case PrivilegeScope.TENANT:
        return new TypeaheadConfig({
          placeholder: 'tkey;admin.user_group_privileges_modal.user.upn.placeholder',
          search: (searchTerm) => {
            return this._microsoft365UserResource.search(this._tenantId, searchTerm.trim()).pipe(
              map((results) => {
                return results.map((result) => result.microsoft365UserRef.id);
              }),
            );
          },
        });
      case PrivilegeScope.GLOBAL:
        return new ZiroTextConfig({
          htmlInputType: HtmlInputType.TEXT,
          placeholder: 'tkey;admin.user_group_privileges_modal.user.upn.placeholder',
        });
    }
  }

  private _getGroupNameField(): ZiroFieldConfig<ZiroSelectConfig> {
    return {
      label: 'tkey;admin.user_group_privileges_modal.group.display_name',
      dataAutomation: 'user-group-privileges-group-name-input',
      required: true,
      disabled: () => {
        return (
          this.isExisting ||
          (this.formData.scope === PrivilegeScope.GLOBAL &&
            this.formData.type === PrivilegeType.GROUP &&
            !this.formData.tenantGuid)
        );
      },
      disabledTooltip: this.isExisting
        ? 'tkey;admin.user_group_privileges_modal.group.existing.disabled'
        : 'tkey;admin.user_group_privileges_modal.group.no_tenant.disabled',
      componentConfig: new ZiroSelectConfig({
        placeholder: 'tkey;admin.user_group_privileges_modal.group.display_name.placeholder',
        minSearchLength: 3,
        asyncOptionsFn: (searchTerm) => {
          return this._microsoftSecurityGroupsTenantSearchResource
            .search(this._tenantId, searchTerm.trim(), false)
            .pipe(
              map((results) => {
                return results.map((result) => result.microsoftSecurityGroupRefJson.displayName);
              }),
            );
        },
      }),
      validation: this.isExisting
        ? []
        : [
            {
              validator: (val: string) => {
                return this._isDuplicate(val) ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
              },
              message: 'tkey;admin.user_group_privileges_modal.group.display_name.unique',
            },
          ],
    };
  }

  private _getGroupTenantGuidField(): ZiroFieldConfig<ZiroSelectConfig> {
    return {
      label: 'tkey;admin.user_group_privileges_modal.group.tenant_guid',
      helpText: 'tkey;admin.user_group_privileges_modal.group.tenant_guid.helptext',
      dataAutomation: 'user-group-privileges-tenant-guid-input',
      required: true,
      disabled: () => this.isExisting,
      disabledTooltip: 'tkey;admin.user_group_privileges_modal.group.existing.disabled',
      componentConfig: new ZiroSelectConfig({
        bindValue: 'value',
        options: this._allTenants.map((tenant) => {
          return {
            label: tenant.tenantName,
            value: tenant.tenantGuid,
          };
        }),
      }),
    };
  }

  private _getRoleRadioOptions(): ZiroRadioButton<PrivilegeRole>[] {
    const buttons: ZiroRadioButton<PrivilegeRole>[] = [];
    if (this.scope === PrivilegeScope.GLOBAL) {
      buttons.push({
        label: 'tkey;admin.user_group_privileges_modal.roles.global_administrator',
        helpText: 'tkey;admin.user_group_privileges_modal.roles.global_administrator.helptext',
        value: PrivilegeRole.GLOBAL_ADMINISTRATOR,
      });
    }
    buttons.push(
      {
        label: 'tkey;admin.user_group_privileges_modal.roles.tenant_administrator',
        helpText: 'tkey;admin.user_group_privileges_modal.roles.tenant_administrator.helptext',
        value: PrivilegeRole.TENANT_ADMINISTRATOR,
      },
      {
        label: 'tkey;admin.user_group_privileges_modal.roles.custom',
        helpText: 'tkey;admin.user_group_privileges_modal.roles.custom.helptext',
        value: PrivilegeRole.CUSTOM,
      },
    );
    return buttons;
  }

  private _getRestrictionFields(): ZiroFieldConfigs {
    return {
      teamsUserManagementRestrictedDialPlanGroupsAccess: {
        dataAutomation: 'user-group-privileges-teams-user-management-restrictions-radio',
        helpText: 'tkey;admin.user_group_privileges_modal.privileges.teams_user_management.restricted.helptext',
        componentConfig: new ZiroRadioConfig({
          alignLeftNoLabel: true,
          inline: true,
          hideLabel: true,
          buttons: [
            {
              label: 'tkey;admin.user_group_privileges_modal.privileges.unrestricted',
              value: PrivilegeRestriction.FULL_ACCESS,
            },
            {
              label: 'tkey;admin.user_group_privileges_modal.privileges.restricted',
              value: PrivilegeRestriction.PARTIAL_ACCESS,
            },
          ],
        }),
        hidden: () => this.formData.role !== PrivilegeRole.CUSTOM || !this.formData.teamsUserManagement,
      },
      teamsUserManagementRestrictedDialPlanGroups: {
        dataAutomation: 'user-group-privileges-teams-user-management-restrictions-select',
        label: 'tkey;admin.user_group_privileges_modal.privileges.teams_user_management.restricted.dpgs',
        componentConfig: new ZiroSelectConfig({
          options: this._dialPlanGroupOptions,
          isMultiSelect: true,
        }),
        validation: [
          {
            validator: (val: ZiroSelectOption[]) => {
              return val && val.length > 0 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
            },
            message:
              'tkey;admin.user_group_privileges_modal.privileges.teams_user_management.restricted.dpgs.validation.none',
          },
        ],
        valExcluded: () =>
          this.formData.role !== PrivilegeRole.CUSTOM ||
          this.formData.scope === PrivilegeScope.GLOBAL ||
          !this.formData.teamsUserManagement ||
          this.formData.teamsUserManagementRestrictedDialPlanGroupsAccess !== PrivilegeRestriction.PARTIAL_ACCESS,
        hidden: () =>
          this.formData.role !== PrivilegeRole.CUSTOM ||
          this.formData.scope === PrivilegeScope.GLOBAL ||
          !this.formData.teamsUserManagement ||
          this.formData.teamsUserManagementRestrictedDialPlanGroupsAccess !== PrivilegeRestriction.PARTIAL_ACCESS,
        disabled: () => !this._dialPlanGroupOptions.length,
        disabledTooltip:
          'tkey;admin.user_group_privileges_modal.privileges.teams_user_management.restricted.dpgs.none_tooltip',
      },
    };
  }

  private _getDialPlanGroupOptions(dialPlanGroups: MicrosoftDialPlanFieldConfig[]): ZiroSelectOption[] {
    return dialPlanGroups.map((dpg) => {
      return {
        value: dpg.id.toString(),
        label: dpg.name,
      };
    });
  }

  private _fetchGroupMembers(tenantId: string, groupName: string): void {
    if (!groupName) {
      this.usersInGroup = [];
      return;
    }
    this.viewMembersState = ViewMembersState.SEARCHING;
    this._microsoftSecurityGroupsTenantSearchResource.get(tenantId, groupName).subscribe((data) => {
      this.usersInGroup = data.members;
      this.viewMembersState = ViewMembersState.IDLE;
    });
  }
}
